import { useState } from 'react';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import FacturacionService from '../../services/FacturacionService';
import { IInvoiceCounterEntidad } from '../../types/EntidadFacturacion';
import { getAnioEnCurso } from '../../utils/Numeracion';

interface CambioAnioNumeracionDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  entidadId: string | null; // null = entidad principal
  serieActual: string; // p.ej. "B2025" — se mantiene el prefijo, solo cambia el año
  pais?: string; // país de la entidad: el año en curso es el de su zona horaria
  onSuccess: (nuevoContador: IInvoiceCounterEntidad) => void;
}

// Reinicio de numeración por cambio de año para una entidad (adicional). Es automático: la
// serie se mantiene, solo cambia el año y el contador vuelve a 0 — a diferencia del cambio de
// NIF, aquí no hay ninguna decisión fiscal que tomar, así que no se le pide nada al usuario
// más que confirmar.
const CambioAnioNumeracionDialog = ({ open, onClose, userId, entidadId, serieActual, pais, onSuccess }: CambioAnioNumeracionDialogProps) => {
  const currentYear = getAnioEnCurso(pais);
  // La serie es siempre prefijo + año de 4 dígitos ("B2025"). Solo se quita el año final: el
  // prefijo puede contener números ("A1"), que no deben perderse.
  const seriePrefix = serieActual.replace(/\d{4}$/, '') || 'S';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatInvoiceNumber = () => `${seriePrefix}${currentYear}-000001`;

  const handleConfirmar = async () => {
    setLoading(true);
    setError(null);
    try {
      const newSerie = `${seriePrefix}${currentYear}`;
      await FacturacionService.updateInvoiceCounter(userId, { entidadId, newSerie, counter: 0 });
      onSuccess({ entidadId, serie: newSerie, counter: 0 });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Cambio de año de facturación</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="body2" sx={{ mb: 2 }}>
          Ha cambiado el año para esta entidad. La serie definida se mantiene, el contador se reinicia
          y la próxima factura emitida será la número 1.
        </Typography>

        <Typography variant="body1">
          Próxima factura a emitir: <b>{formatInvoiceNumber()}</b>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={onClose}>Cancelar</Button>
        <Button disabled={loading} variant="contained" onClick={handleConfirmar}>
          {loading ? <CircularProgress size={20} /> : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CambioAnioNumeracionDialog;
